//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import ItemMasterModel from '../models/item-master.model';

/**
 * @service ItemMasterService
 * @description Service for managing the Item Master inventory and product definitions within the Material Management System (MMS).
 * This service handles the creation, retrieval, updating, and validation of items, including products, feed types, and feed mixes.
 * It interacts with the backend API to manage item metadata, UOM conversions, and business-specific item associations.
 *
 * @feature 1. Retrieve all item master records or filter by business/farmer
 * @feature 2. Manage products, feed types, and feed mixes specifically
 * @feature 3. Add and update item master records with role-based logic
 * @feature 4. Validate Unit of Measure (UOM) conversions
 * @feature 5. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<ItemMasterModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 * Implements role-based logic using `localStorage.loginPersonType` to determine query paths and payload structures.
 *
 * @service-endpoint /mms/itemmaster/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE' or other role (e.g., Farmer, Admin), affecting API query paths and payload sanitization.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ItemMasterService extends Service<ItemMasterModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/itemmaster/');
  }

  /**
   * @method getAllItemMaster
   * @async
   * @description Fetch all item master records where `isBH` is 0 (not a Business House specific item).
   *
   * @returns {Promise<Array<ItemMasterModel>>} Promise resolving to an array of ItemMasterModel objects
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   *
   * @example
   * const items = await itemMasterService.getAllItemMaster();
   */
  public getAllItemMaster() {
    return new Promise<Array<ItemMasterModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ItemMasterModel>>(`${this.baseUrl}${this.serviceUrl}allItemMaster/isBH/0`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllItemMasterbyFarmer
   * @async
   * @description Fetch item master records associated with a specific farmer or business.
   *
   * The API endpoint path is dynamically constructed based on the logged-in user's role (`loginPersonType`).
   * - If 'EMPLOYEE': Query includes `businessId/{id}`.
   * - If other: Query uses `businessId//` (implying context inference on backend or specific API behavior).
   *
   * @param {string} businessId - The unique identifier of the business/farmer
   * @returns {Promise<Array<ItemMasterModel>>} Promise resolving to an array of ItemMasterModel objects
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public getAllItemMasterbyFarmer(businessId: string) {
    let queryPath = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryPath = `businessId/${businessId}/isBH/0`;
    } else {
      queryPath = `businessId//isBH/0`;
    }
    return new Promise<Array<ItemMasterModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ItemMasterModel>>(`${this.baseUrl}${this.serviceUrl}${queryPath}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllProductsByBusinessId
   * @async
   * @description Fetch product-specific item master records for a business.
   *
   * Similar to `getAllItemMasterbyFarmer`, this method adjusts the URL path based on `loginPersonType`.
   *
   * @param {string} businessId - The unique identifier of the business
   * @returns {Promise<Array<ItemMasterModel>>} Promise resolving to an array of ItemMasterModel objects representing products
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
   public getAllProductsByBusinessId(businessId: string) {
    let queryPath = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryPath = `businessId/${businessId}/isBH/0`;
    } else {
      queryPath = `businessId//isBH/0`;
    }
    return new Promise<Array<ItemMasterModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ItemMasterModel>>(`${this.baseUrl}${this.serviceUrl}product/${queryPath}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method updateItemMaster
   * @async
   * @description Update an existing item master record.
   *
   * @logic
   * Checks `localStorage.loginPersonType`. If the user is NOT an 'EMPLOYEE', the `business_id` property
   * is removed from the payload before sending to prevent unauthorized business ID modification.
   *
   * @param {ItemMasterModel} itemmaster - The item master data to update
   * @returns {Promise<Array<ItemMasterModel>>} Promise resolving to the updated data
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public updateItemMaster(itemmaster: ItemMasterModel) {
    if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete itemmaster.business_id;
    }
    return new Promise<Array<ItemMasterModel>>((resolve, reject) => {
      const request = this.httpClient.put<Array<ItemMasterModel>>(`${this.baseUrl}${this.serviceUrl}updateItemMaster`, itemmaster, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addItemMaster
   * @async
   * @description Create a new item master record.
   *
   * @logic
   * Checks `localStorage.loginPersonType`. If the user is NOT an 'EMPLOYEE', the `business_id` property
   * is removed from the payload before sending.
   *
   * @param {ItemMasterModel} itemmaster - The new item master data
   * @returns {Promise<ItemMasterModel>} Promise resolving to the created ItemMasterModel
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public addItemMaster(itemmaster: ItemMasterModel) {
    if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete itemmaster.business_id;
    }
    return new Promise<ItemMasterModel>((resolve, reject) => {
      const request = this.httpClient.post<ItemMasterModel>(`${this.baseUrl}${this.serviceUrl}addItemMaster`, itemmaster, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears ongoingRequests array
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
